import PreventiveOutWidgetItems from '@/models/PreventiveOutWidgetItems';
import RequestInterventionWidgetItems from '@/models/RequestInterventionWidgetItems';
import EstimateWidgetItems from '@/models/EstimateWidgetItems';
import { TableHeader } from '@/models/TableHeader';
import i18n from '@/plugins/i18n';
import { SortType } from '@/models/SortType';
import { EtatsTravaux, InterventionNonPreventive } from '@/models/InterventionNonPreventive';
import dayjs from 'dayjs';
import { EquipementItems } from '@/models/EquipementItems';
import { InterventionPreventive } from '@/models/InterventionPreventive';
import { addMonths, format, subMonths } from 'date-fns';
import { CaracteristiqueArticle } from '@/models/CaracteristiqueArticle';
import { Operation } from '@/models/Operation';
import { Devis, EtatDevis } from '@/models/Devis';
import { DemandeIntervention } from '@/models/DemandeIntervention';
import { DocumentReglementaire } from '@/models/DocumentReglementaire';
import { AnalyseDeRisqueDTO } from '@/models/AnalyseDeRisqueDTO';
import { PriseEnChargeDTO } from '@/models/PriseEnChargeDTO';
import { TypeDocumentReglementaire } from '@/models/TypeDocumentReglementaire';
import { PreventiveChartWidgetItems, PreventiveCircleWidgetItems } from '@/models/PreventiveWidgetItems';

export interface Tab {
    id: string;
    label: string;
}

export interface HistoriqueDemandeInterventionState {
    demandesIntervention: DemandeIntervention[];

    tableHeader: TableHeader[];

    page: number;
    count: number;
    nbOfPages: number;
    scroll: number;

    sort?: {
        sortData: string;
        sortStatus: SortType;
    };
    filter: {
        statusList: string[];
        demandeInterventionId: string;
        createdDateFrom: string;
        createdDateTo: string;
    };

    isLoading: boolean;
    error?: Error;
    success: boolean;

    attachment: any;
    attachmentType: string;
    isAttachmentLoading: boolean;

    isExportXlsLoading: boolean;
}
export interface HistoriqueTravauxState {
    items: InterventionNonPreventive[];
    tableHeader: TableHeader[];
    page: number;
    nbOfPages: number;
    count: number;
    sort?: {
        sortData: string;
        sortStatus: SortType;
    };
    filter: {
        statusList: string[];
        interventionId: string;
        predictedDateFrom: string;
        predictedDateTo: string;
    };
    isLoading: boolean;
    exportXlsIsLoading: boolean;
}
export interface PreventifState {
    items: InterventionPreventive[];
    tableHeader: TableHeader[];
    page: number;
    nbOfPages: number;
    count: number;
    sort?: {
        sortData: string;
        sortStatus: SortType;
    };
    filter: {
        statusList: string[];
        interventionId: string;
        predictedDateFrom: string;
        predictedDateTo: string;
    };
    isLoading: boolean;
    exportXlsIsLoading: boolean;
}
export interface HistoriqueDevisState {
    devis: Devis[];

    tableHeader: TableHeader[];

    page: number;
    count: number;
    nbOfPages: number;
    scroll: number;

    sort: {
        sortData: string;
        sortStatus: SortType;
    };

    filter: {
        filterStatus: string[];
        filterDevisId: string;
        filterNumero: string;
        filterNumFactures: string;
    };

    isLoading: boolean;
    error?: Error;
    success: boolean;

    bonDeCommandeFile: File[];

    isExportXlsLoading: boolean;
}

export interface DocumentReglementaireState {
    tableHeader: TableHeader[];
    tableAdrHeader: TableHeader[];
    tablePecHeader: TableHeader[];
    page: number;
    nbOfPages: number;
    scroll: number;
    count: number;
    documentReglementaire: DocumentReglementaire[];
    adr: AnalyseDeRisqueDTO[];
    prisesEnCharge: PriseEnChargeDTO[];
    sort?: {
        sortData: string;
        sortStatus: SortType;
    };
    isLoading: boolean;
    isZipLoading: boolean;
    listTypesIsLoading: boolean;
    filter: {
        filterDocumentType: TypeDocumentReglementaire[];
    };
    documentIdsToDownload: number[];
    error?: Error;
    nombreDocumentModal: number;
    typesDocumentsReglementaires: TypeDocumentReglementaire[];
}

export interface EquipementState {
    nonPreventiveStats: PreventiveOutWidgetItems;
    preventiveStatsCircle: PreventiveCircleWidgetItems;
    preventiveStatsChart: PreventiveChartWidgetItems;
    devisStats: EstimateWidgetItems;
    demandesInterventionStats: RequestInterventionWidgetItems;
    tabs: Tab[];
    caracteristiquesHeaders: TableHeader[];
    maintenancesHeaders: TableHeader[];
    historiqueTravaux: HistoriqueTravauxState;
    interventionsPreventives: PreventifState;
    isLoading: boolean;
    isNonPreventiveStatsLoading: boolean;
    isPreventiveStatsCircleLoading: boolean;
    isPreventiveStatsChartLoading: boolean;
    isDevisStatsLoading: boolean;
    isDemandesInterventionStatsLoading: boolean;
    error: boolean;
    detailsEquipement: EquipementItems;
    isCaracteristiquesLoading: boolean;
    isOperationMaintenanceLoading: boolean;
    caracteristiquesArticle: CaracteristiqueArticle[];
    operations: Operation[];
    historiqueDevis: HistoriqueDevisState;
    activeTabId: string;
    historiqueDemandesInterventions: HistoriqueDemandeInterventionState;
    documentReglementaire: DocumentReglementaireState;
}

export const state: EquipementState = {
    nonPreventiveStats: {
        nbEnCours: 0,
        nbRealisees: 0,
        nbARealiser: 0,
        debutPeriode: '',
        finPeriode: '',
    },
    preventiveStatsCircle: {
        inProgressTotal: 0,
        achievedTotal: 0,
        awaitingRealizationTotal: 0,
    },
    preventiveStatsChart: {
        inProgress: [],
        achieved: [],
        awaitingRealization: [],
    },

    devisStats: {
        nbPublies: 0,
        nbEnCommande: 0,
        nbRealises: 0,
        nbEnCours: 0,
    },
    demandesInterventionStats: {
        nbPlanifie: 0,
        nbNonPlanifie: 0,
        nbEnCours: 0,
        nbTraite: 0,
    },
    detailsEquipement: {
        id: 0,
        libelle: '',
        phaseId: 0,
        phaseLibelle: '',
        groupeId: 0,
        groupeLibelle: '',
        sousGroupeId: 0,
        sousGroupeLibelle: '',
        adresseProjetId: 0,
    },
    tabs: [
        { id: 'dashboard', label: 'equipement.onglet.dashboard' },
        { id: 'historiqueDemandeInterventions', label: 'equipement.onglet.interventionRequest' },
        { id: 'historiqueTravaux', label: 'equipement.onglet.travaux' },
        { id: 'interventionsPreventives', label: 'equipement.onglet.preventif' },
        { id: 'historiqueDevis', label: 'equipement.onglet.devis' },
        { id: 'caracteristiques', label: 'equipement.onglet.caracteristiques' },
        { id: 'gammeMaintenance', label: 'equipement.onglet.maintenances' },
        { id: 'docReglementaires', label: 'equipement.onglet.docReglementaire' },
    ],
    activeTabId: 'dashboard',
    isNonPreventiveStatsLoading: false,
    isPreventiveStatsCircleLoading: false,
    isPreventiveStatsChartLoading: false,
    isDevisStatsLoading: false,
    isDemandesInterventionStatsLoading: false,

    historiqueDemandesInterventions: {
        demandesIntervention: [],

        tableHeader: [
            {
                label: 'intervention.request.id',
                sortData: 'id',
                sortStatus: SortType.NONE,
                width: '12%',
            },
            {
                label: 'intervention.request.object',
                sortData: 'libelle',
                sortStatus: SortType.NONE,
                width: '25%',
            },

            {
                label: 'intervention.request.datecreation',
                sortData: 'creationDate',
                sortStatus: SortType.DESC,
                width: '8%',
            },
            {
                label: 'intervention.request.delais',
                sortData: 'delai',
                sortStatus: SortType.INACTIVE,
                width: '10%',
            },
            {
                label: 'intervention.request.status',
                sortData: 'statut',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'intervention.request.pdf',
                sortData: 'pdfExist',
                sortStatus: SortType.INACTIVE,
                width: '5%',
            },
        ],

        page: 0,
        count: 0,
        nbOfPages: 0,
        scroll: 0,

        sort: {
            sortData: 'creationDate',
            sortStatus: SortType.DESC,
        },
        filter: {
            statusList: [],
            demandeInterventionId: '',
            createdDateFrom: dayjs().add(-1, 'y').format('YYYY-MM-DD'),
            createdDateTo: dayjs().format('YYYY-MM-DD'),
        },

        isLoading: false,
        success: false,

        attachment: '',
        attachmentType: '',
        isAttachmentLoading: false,

        isExportXlsLoading: false,
    },

    historiqueTravaux: {
        items: [],
        tableHeader: [
            {
                label: 'intervention.id',
                sortData: 'id',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'intervention.objet',
                sortData: 'objet',
                sortStatus: SortType.NONE,
                width: '24%',
            },
            {
                label: 'intervention.request.type',
                sortData: 'type',
                sortStatus: SortType.NONE,
                width: '15%',
            },
            {
                label: 'intervention.dateDebutPrevue',
                sortData: 'dateDebutPrevue',
                sortStatus: SortType.DESC,
                width: '10%',
            },
            {
                label: 'intervention.dateDebut',
                sortData: 'dateDebutReelle',
                sortStatus: SortType.NONE,
                width: '9%',
            },
            {
                label: 'intervention.dateFin',
                sortData: 'dateFinReelle',
                sortStatus: SortType.NONE,
                width: '9%',
            },

            {
                label: 'dataList.alarmante',
                sortData: 'isAlarmante',
                sortStatus: SortType.INACTIVE,
                width: '5%',
            },
            {
                label: 'intervention.request.status',
                sortData: 'etat',
                sortStatus: SortType.NONE,
                width: '9%',
            },
            {
                label: 'intervention.request.pdf',
                sortData: 'pdfExist',
                sortStatus: SortType.INACTIVE,
                width: '4%',
            },
        ],
        page: 0,
        nbOfPages: 0,
        count: 0,
        sort: {
            sortData: 'dateDebutPrevue',
            sortStatus: SortType.DESC,
        },
        filter: {
            statusList: Object.values(EtatsTravaux),
            interventionId: '',
            predictedDateFrom: dayjs().add(-1, 'y').format('YYYY-MM-DD'),
            predictedDateTo: dayjs().format('YYYY-MM-DD'),
        },
        isLoading: false,
        exportXlsIsLoading: false,
    },
    interventionsPreventives: {
        items: [],
        tableHeader: [
            {
                label: 'intervention.id',
                sortData: 'id',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'intervention.request.type',
                sortData: 'type',
                sortStatus: SortType.NONE,
                width: '24%',
            },
            {
                label: 'label.periodicite',
                sortData: 'periodicite',
                sortStatus: SortType.NONE,
                width: '15%',
            },
            {
                label: 'intervention.dateDebutPrevue',
                sortData: 'dateDebutPrevue',
                sortStatus: SortType.DESC,
                width: '10%',
            },
            {
                label: 'intervention.dateFin',
                sortData: 'dateFin',
                sortStatus: SortType.INACTIVE,
                width: '10%',
            },
            {
                label: 'intervention.request.status',
                sortData: 'etat',
                sortStatus: SortType.NONE,
                width: '9%',
            },
            {
                label: 'intervention.request.pdf',
                sortData: 'pdfExist',
                sortStatus: SortType.INACTIVE,
                width: '4%',
            },
        ],
        page: 0,
        nbOfPages: 0,
        count: 0,
        sort: {
            sortData: 'dateDebutPrevue',
            sortStatus: SortType.DESC,
        },
        filter: {
            statusList: Object.values(EtatsTravaux),
            interventionId: '',
            predictedDateFrom: format(subMonths(new Date(), 6), 'yyyy-MM-dd'),
            predictedDateTo: format(addMonths(new Date(), 6), 'yyyy-MM-dd'),
        },
        isLoading: false,
        exportXlsIsLoading: false,
    },

    historiqueDevis: {
        devis: [],
        sort: {
            sortData: 'dateChiffrage',
            sortStatus: SortType.DESC,
        },
        tableHeader: [
            {
                label: 'devis.id',
                sortData: 'numero',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'devis.object',
                sortData: 'objet',
                sortStatus: SortType.NONE,
                width: '28%',
            },
            {
                label: 'devis.numero',
                sortData: 'numCommande',
                sortStatus: SortType.INACTIVE,
                width: '14%',
            },
            {
                label: 'devis.demande.id',
                sortData: 'demandeDevisId',
                sortStatus: SortType.NONE,
                width: '7%',
            },
            {
                label: 'devis.prix',
                sortData: 'prix',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'devis.montantFacture',
                sortData: 'montantFacture',
                sortStatus: SortType.INACTIVE,
                width: '10%',
            },
            {
                label: 'devis.numFactures',
                sortData: 'factures.numero',
                sortStatus: SortType.INACTIVE,
                width: '11%',
            },
            {
                label: 'devis.datePublication',
                sortData: 'dateChiffrage',
                sortStatus: SortType.DESC,
                width: '11%',
            },
            {
                label: 'devis.etatDevis',
                sortData: 'etatDevis',
                sortStatus: SortType.NONE,
                width: '10%',
            },
        ],
        page: 0,
        count: 0,
        nbOfPages: 0,
        scroll: 0,
        filter: {
            filterStatus: Object.values(EtatDevis),
            filterDevisId: '',
            filterNumero: '',
            filterNumFactures: '',
        },

        isLoading: false,
        success: false,

        bonDeCommandeFile: [],

        isExportXlsLoading: false,
    },

    //Maintenances / Caracteristiques
    isOperationMaintenanceLoading: false,
    isCaracteristiquesLoading: false,
    caracteristiquesArticle: [],
    operations: [],

    caracteristiquesHeaders: [
        {
            label: i18n.global.t('modale.equipment.characteristic.label'),
            sortStatus: SortType.INACTIVE,
            sortData: '',
            width: '33%',
        },
        {
            label: i18n.global.t('modale.equipment.characteristic.value'),
            sortStatus: SortType.INACTIVE,
            sortData: '',
            width: '33%',
        },
        {
            label: i18n.global.t('modale.equipment.characteristic.unit'),
            sortStatus: SortType.INACTIVE,
            sortData: '',
            width: '33%',
        },
    ],
    maintenancesHeaders: [
        {
            label: i18n.global.t('modale.equipment.gamme.maintenance.operations'),
            sortStatus: SortType.INACTIVE,
            sortData: '',
            width: '70%',
        },
        {
            label: i18n.global.t('modale.equipment.gamme.maintenance.periodicite'),
            sortStatus: SortType.INACTIVE,
            sortData: '',
            width: '30%',
        },
    ],

    documentReglementaire: {
        documentReglementaire: [],
        adr: [],
        prisesEnCharge: [],
        tableHeader: [
            {
                label: '',
                sortData: 'uniqueDocumentId',
                sortStatus: SortType.INACTIVE,
                width: '5%',
            },
            {
                label: 'document.reglementaire.data.libelle',
                sortData: 'interventionId',
                sortStatus: SortType.NONE,
                width: '10%',
            },
            {
                label: 'document.reglementaire.data.type',
                sortData: 'typesDocumentsReglementaire',
                sortStatus: SortType.INACTIVE,
                width: '15%',
            },
            {
                label: 'document.reglementaire.data.date',
                sortData: 'dateIntervention',
                sortStatus: SortType.DESC,
                width: '10%',
            },
        ],
        tableAdrHeader: [
            {
                label: '',
                sortData: 'uniqueDocumentId',
                sortStatus: SortType.INACTIVE,
                width: '5%',
            },
            {
                label: 'analyse.risque.table.id',
                sortData: 'id',
                sortStatus: SortType.DESC,
                width: '15%',
            },
            {
                label: 'analyse.risque.table.date.creation',
                sortData: 'dateCreation',
                sortStatus: SortType.NONE,
                width: '20%',
            },
            {
                label: 'analyse.risque.table.date.debut.validite',
                sortData: 'dateDebut',
                sortStatus: SortType.NONE,
                width: '20%',
            },
            {
                label: 'analyse.risque.table.date.fin.validite',
                sortData: 'dateFin',
                sortStatus: SortType.NONE,
                width: '20%',
            },
        ],
        tablePecHeader: [
            {
                label: 'priseEnCharge.table.libelle',
                sortData: 'titre',
                sortStatus: SortType.INACTIVE,
                width: '30%',
            },
            {
                label: 'priseEnCharge.table.projet',
                sortData: 'libelleChantier',
                sortStatus: SortType.INACTIVE,
                width: '30%',
            },
            {
                label: 'priseEnCharge.table.date.miseAjourjour',
                sortData: 'dateMiseAJour',
                sortStatus: SortType.INACTIVE,
                width: '25%',
            },
            {
                label: 'priseEnCharge.table.document.pdf',
                sortData: 'pdfExist',
                sortStatus: SortType.INACTIVE,
                width: '15%',
                textalign: 'center',
            },
        ],
        page: 0,
        nbOfPages: 0,
        scroll: 0,
        count: 0,

        sort: {
            sortData: 'dateIntervention',
            sortStatus: SortType.DESC,
        },
        filter: {
            filterDocumentType: [],
        },
        documentIdsToDownload: [],

        isLoading: false,
        isZipLoading: false,
        listTypesIsLoading: false,
        nombreDocumentModal: 0,
        typesDocumentsReglementaires: [],
    },
    isLoading: false,
    error: false,
};
